import { observable, action, computed, makeObservable } from 'mobx';
import flatten from 'lodash/flatten';
import omit from 'lodash/omit';

import {
    EntryTypeSectionData,
    EntryTypeSectionBlockSettingsData,
    EntryTypeSectionSettingsData,
    EntryTypeBlockData,
    Optional,
} from 'chaika-frontend-types/frontend-cms';
import type { SectionParams, TranslationsDataParams } from 'chaika-types';
import { Locale } from 'chaika-enums';

import { changePositionInMapObject } from '@utils/changePositionInMapObject';
import { toArrayFromIterable } from '@utils/toArrayFromIterable';

import { EntryTypeBlock } from './EntryTypeBlock';
import { EntryTypeField } from './EntryTypeField';
import { EntryMedicalCaseElements } from './types';
import { translate } from '@chaika/i18n/helper';

interface CreateParams extends EntryTypeSectionData {
    id: string;
}

export class EntryTypeSection {
    @observable public id: string;
    @observable public title: string;
    @observable public collapsible: boolean;
    @observable public titleTranslations = new Map<Locale, TranslationsDataParams>();
    @observable public settings?: EntryTypeSectionSettingsData;
    @observable private blocks_: Map<string, EntryTypeBlock> = new Map();

    constructor({ id, title, collapsible = true, blocks, settings, titleTranslations = [] }: CreateParams) {
        makeObservable(this);

        this.id = id;
        this.title = title;
        this.collapsible = collapsible;
        this.settings = settings;

        titleTranslations.forEach(title => this.titleTranslations.set(title.locale, title));

        blocks.forEach(block => {
            this.blocks_.set(block.id, new EntryTypeBlock({ sectionTitle: title, ...block }));
        });
    }

    @computed
    public get translationLocales(): string[] {
        return Array.from(this.titleTranslations.keys());
    }

    @computed
    public get titleTranslationsValues(): TranslationsDataParams[] {
        return toArrayFromIterable(this.titleTranslations);
    }

    @computed
    public get saveParams(): SectionParams {
        return {
            title: this.title,
            collapsible: this.collapsible,
            settings: this.settings,
            titleTranslations: this.titleTranslationsValues,
            blocks: this.blocks.map(block => ({
                id: block.id,
                version: block.version,
            })),
        };
    }

    @computed
    public get blockIds(): string[] {
        return Array.from(this.blocks_.keys());
    }

    @computed
    public get blockWithDataIds(): string[] {
        return this.blocks.filter(block => block.hasData).map(block => block.id);
    }

    @computed
    public get blocks(): EntryTypeBlock[] {
        return Array.from(this.blocks_.values());
    }

    @computed
    public get fields(): EntryTypeField[] {
        const fields: EntryTypeField[][] = [];

        this.blocks_.forEach(block => {
            fields.push(block.fields);
        });

        return flatten(fields);
    }

    @computed
    public get isHighlighted(): boolean {
        return !this.hasData && this.title.toLowerCase() === translate('services').toLowerCase();
    }

    @computed
    public get hasData(): boolean {
        return this.blocks.some(item => item.hasData);
    }

    @computed
    public get hasDataAndAlwaysShow(): boolean {
        return this.blocks.some(block => block.alwaysShow || block.hasData);
    }

    @computed
    public get showOnPrint(): boolean {
        return (
            this.title.toLowerCase() !== translate('services').toLowerCase() &&
            this.blocks.some(item => item.hasData || item.isEnableOnViewMode)
        );
    }

    @computed
    public get isMedicalCaseSection(): boolean {
        return !!this.blocks.find(({ fields }) =>
            fields.find(({ name }) => name === EntryMedicalCaseElements.MedicalCasesSection),
        );
    }

    @computed
    public get isEnableOnViewMode(): boolean {
        return this.blocks.some(block => block.isEnableOnViewMode);
    }

    @computed
    public get enableOnViewModeBlocks(): EntryTypeBlock[] {
        return this.blocks.filter(block => block.isEnableOnViewMode);
    }

    public isAllowForEntryType(typeId: string): boolean {
        const allowForEntryType = this.settings?.allowForEntryTypes ?? [];
        return allowForEntryType.length === 0 || allowForEntryType.includes(typeId);
    }

    public getBlock(id: string): Optional<EntryTypeBlock> {
        return this.blocks_.get(id);
    }

    @action
    public changeBlockPosition(oldPosition: number, newPosition: number) {
        this.blocks_ = changePositionInMapObject(this.blocks_, oldPosition, newPosition);
    }

    @action
    public setTitleTranslations(params: TranslationsDataParams): void {
        this.titleTranslations.set(params.locale, params);
    }

    public getBlockSettings(blockId: string): Optional<EntryTypeSectionBlockSettingsData> {
        const settings = this.settings ?? ({} as EntryTypeSectionSettingsData);
        const blockSettings = settings.blockSettings ?? {};

        return blockSettings[blockId];
    }

    public getBlockWidth(blockId: string): Optional<number> {
        const blockSettings = this.getBlockSettings(blockId);

        return blockSettings?.width;
    }

    public getBlockAlign(blockId: string): Optional<string> {
        const blockSettings = this.getBlockSettings(blockId);

        return blockSettings?.align;
    }

    @action
    public updateBlockSettings(blockId: string, blockSettingsParams: Partial<EntryTypeSectionBlockSettingsData>): void {
        const settings = this.settings ?? ({} as EntryTypeSectionSettingsData);
        const blockSettings = settings.blockSettings ?? {};

        this.settings = {
            ...settings,
            blockSettings: {
                ...blockSettings,
                [blockId]: {
                    ...(blockSettings[blockId] ?? {}),
                    ...blockSettingsParams,
                },
            },
        };
    }

    @action
    public resetBlockWidth(blockId: string): void {
        const settings = this.settings ?? ({} as EntryTypeSectionSettingsData);
        const blockSettings = omit(settings.blockSettings ?? {}, blockId);

        this.settings = {
            ...settings,
            blockSettings,
        };
    }

    @action
    public removeBlock(blockId: string) {
        this.blocks_.delete(blockId);
    }

    @action
    public addBlock(block: EntryTypeBlockData) {
        this.blocks_.set(
            block.id,
            new EntryTypeBlock({
                sectionTitle: this.title,
                ...block,
            }),
        );
    }
}
